import { type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";

type NavLink = {
  title: string;
  badge?: string;
  icon?: React.ElementType;
  url: string;
  external?: boolean;
};

type NavGroupProps = {
  title: string;
  items: NavLink[];
};

export function NavGroup({ title, items }: NavGroupProps) {
  const href = useLocation();
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{title}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const key = `${item.title}-${item.url}`;

          return <SidebarMenuLink key={key} item={item} href={href.pathname} />;
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavBadge({ children }: { children: ReactNode }) {
  return (
    <Badge variant="secondary" className="rounded-full px-1 py-0 text-xs">
      {children}
    </Badge>
  );
}

function SidebarMenuLink({ item, href }: { item: NavLink; href: string }) {
  const { setOpenMobile } = useSidebar();

  const extraProps = item.external ? { target: "_blank" } : {};

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={checkIsActive(href, item)} tooltip={item.title}>
        <Link to={item.url} onClick={() => setOpenMobile(false)} {...extraProps}>
          {item.icon && <item.icon />}
          <span>{item.title}</span>
          {item.badge && <NavBadge>{item.badge}</NavBadge>}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function checkIsActive(href: string, item: NavLink, mainNav = false) {
  return (
    href === item.url || // /endpoint?search=param
    href.split("?")[0] === item.url || // endpoint
    (mainNav && href.split("/")[1] !== "" && href.split("/")[1] === item?.url?.split("/")[1])
  );
}
